import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { TamilNameField } from '@/components/TamilNameField'
import { useEffect, useMemo, useState } from 'react'
import { useMenuCategories, useMenuItems, useSaveMenuCategory, useSaveMenuItem } from '@/api/hooks'
import type { MenuCategory as MenuCategoryRow } from '@/api/types'
import type { MenuItem as MenuItemType } from '@/api/types'
import { ErrorBanner } from '@/components/common'

const DIET_TYPES = ['VEG', 'NON_VEG', 'VEGAN', 'JAIN', 'EGG']
const SERVING_UNITS = ['NOS', 'PLATE', 'PORTION', 'GRAM', 'ML', 'PIECE', 'KG', 'LITRE']
/** What a side dish weighs when nobody has said, in grams. */
const DEFAULT_SIDE_GRAMS = 50

/** What a weight can be measured in. Solids by mass, liquids by volume. */
const WEIGHT_UNITS = [
  { value: 'GRAM', label: 'g' },
  { value: 'KG', label: 'kg' },
  { value: 'ML', label: 'ml' },
  { value: 'LITRE', label: 'l' },
]

type Draft = {
  categoryId: string
  itemCode: string
  name: string
  nameLocal: string
  description: string
  dietType: string
  standardServingQty: string
  servingUnit: string
  weight: string
  weightUnit: string
  /** Dishes served alongside this one, with how many grams of each. */
  sideDishes: { sideItemId: string; grams: string }[]
  sellingPrice: string
  costEstimate: string
  taxRatePercent: string
  /** INCLUSIVE | EXCLUSIVE — how the selling price is meant. */
  pricing: string
  hsnCode: string
  liveCounter: boolean
}

const BLANK: Draft = {
  categoryId: '',
  itemCode: '',
  name: '',
  nameLocal: '',
  description: '',
  dietType: 'VEG',
  standardServingQty: '1',
  servingUnit: 'NOS',
  weight: '',
  weightUnit: 'GRAM',
  sideDishes: [],
  sellingPrice: '',
  costEstimate: '',
  taxRatePercent: '5',
  pricing: 'EXCLUSIVE',
  hsnCode: '',
  liveCounter: false,
}

/** Create or edit one dish in the catalogue quotations are built from. */
export function MenuItemDialog({
  open,
  item,
  onClose,
}: {
  open: boolean
  item?: MenuItemType | null
  onClose: () => void
}) {
  const { data: categories } = useMenuCategories()
  // Only what a side may be drawn from; the category master decides.
  // Searched on the server as it is typed. Asking for a fixed page and
  // filtering it here quietly hid whole categories: the codes sort by length,
  // so a company with 764 dishes got one of its 75 semi-finished goods in the
  // first 500 and none of the rest could be chosen at all.
  const [sideTerm, setSideTerm] = useState('')
  const { data: allItems, isFetching: findingSides } = useMenuItems({
    q: sideTerm.trim() || undefined,
    offeredAsSideDish: true,
    size: 50,
  })
  const save = useSaveMenuItem()
  const [draft, setDraft] = useState<Draft>(BLANK)

  // A dish cannot be its own side, so the item being edited is not on offer.
  const [chosenSides, setChosenSides] = useState<MenuItemType[]>([])
  const sideOptions = useMemo(() => {
    const byId = new Map<string, MenuItemType>()
    chosenSides.forEach((side) => byId.set(side.id, side))
    ;(allItems?.content ?? []).forEach((option) => byId.set(option.id, option))
    return [...byId.values()].filter((option) => option.id !== item?.id)
  }, [allItems, chosenSides, item?.id])

  // Reload the form whenever a different item is opened for editing.
  useEffect(() => {
    if (!open) return
    // The sides already saved against it, so they are listed before anybody
    // searches. The picker only shows the code and the name.
    setChosenSides(
      (item?.sideDishes ?? []).map(
        (side) =>
          ({
            id: side.sideItemId,
            itemCode: side.itemCode,
            name: side.name,
          }) as MenuItemType,
      ),
    )
    setSideTerm('')
    setDraft(
      item
        ? {
            categoryId: item.categoryId,
            itemCode: item.itemCode,
            name: item.name,
          nameLocal: item.nameLocal ?? '',
            description: '',
            dietType: item.dietType,
            standardServingQty: String(item.standardServingQty ?? 1),
            servingUnit: item.servingUnit,
            weight: item.weight == null ? '' : String(item.weight),
            weightUnit: item.weightUnit || 'GRAM',
            sideDishes: (item.sideDishes ?? []).map((side) => ({
              sideItemId: side.sideItemId,
              grams: String(side.quantityGrams ?? DEFAULT_SIDE_GRAMS),
            })),
            sellingPrice: String(item.sellingPrice ?? ''),
            costEstimate: item.costEstimate == null ? '' : String(item.costEstimate),
            taxRatePercent: String(item.taxRatePercent ?? 5),
            // Nothing on this dialog offers "as the company sets" any more, so
            // an item saved from before that choice existed reads as
            // Excluding GST here rather than showing a value with no option
            // behind it.
            pricing: item.priceInclusiveTax ? 'INCLUSIVE' : 'EXCLUSIVE',
            hsnCode: item.hsnCode ?? '',
            liveCounter: item.liveCounter,
          }
        : BLANK,
    )
    // Keyed on which item is open, not on the object carrying it: the list
    // behind this dialog refetches, and a new object of the same dish would
    // otherwise reset the form - and the search box - mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id])

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }))

  const valid = Boolean(draft.categoryId && draft.itemCode.trim() && draft.name.trim() && draft.servingUnit)

  const submit = () =>
    save.mutate(
      {
        id: item?.id,
        body: {
          categoryId: draft.categoryId,
          itemCode: draft.itemCode.trim(),
          name: draft.name.trim(),
          // Sent even when empty, so clearing it clears the master rather than
          // leaving yesterday's Tamil on a dish that has been renamed.
          nameLocal: draft.nameLocal.trim(),
          description: draft.description || undefined,
          dietType: draft.dietType,
          standardServingQty: Number(draft.standardServingQty || 1),
          servingUnit: draft.servingUnit,
          weight: draft.weight ? Number(draft.weight) : undefined,
          weightUnit: draft.weightUnit,
          sideDishes: draft.sideDishes.map((side) => ({
            sideItemId: side.sideItemId,
            quantityGrams: Number(side.grams || DEFAULT_SIDE_GRAMS),
          })),
          sellingPrice: draft.sellingPrice ? Number(draft.sellingPrice) : 0,
          costEstimate: draft.costEstimate ? Number(draft.costEstimate) : undefined,
          taxRatePercent: draft.taxRatePercent ? Number(draft.taxRatePercent) : undefined,
          pricing: draft.pricing,
          // Sent even when empty, so clearing it clears the master.
          hsnCode: draft.hsnCode.trim(),
          liveCounter: draft.liveCounter,
        },
      },
      { onSuccess: onClose },
    )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{item ? `Edit ${item.name}` : 'New menu item'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              required
              label="Category"
              value={draft.categoryId}
              onChange={(event) => set({ categoryId: event.target.value })}
            >
              {(categories ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              required
              label="Item code"
              value={draft.itemCode}
              onChange={(event) => set({ itemCode: event.target.value.toUpperCase() })}
              disabled={Boolean(item)}
              helperText={item ? 'Codes cannot change once used' : 'Unique, e.g. MI101'}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Diet"
              value={draft.dietType}
              onChange={(event) => set({ dietType: event.target.value })}
            >
              {DIET_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.replace(/_/g, ' ').toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              label="Name"
              value={draft.name}
              onChange={(event) => set({ name: event.target.value })}
            />
          </Grid>
          {/* What the menu sheet prints when it is printed in Tamil. Left
              empty, the sheet falls back to the name beside it, so a catalogue
              only half translated still prints. */}
          <Grid item xs={12} md={4}>
            <TamilNameField
              value={draft.nameLocal}
              onChange={(nameLocal) => set({ nameLocal })}
              fromEnglish={draft.name}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Weight"
              value={draft.weight}
              onChange={(event) => set({ weight: event.target.value })}
              helperText="Leave blank if not weighed"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              select
              fullWidth
              label="Weight unit"
              value={draft.weightUnit}
              onChange={(event) => set({ weightUnit: event.target.value })}
            >
              {WEIGHT_UNITS.map((unit) => (
                <MenuItem key={unit.value} value={unit.value}>
                  {unit.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Serving quantity"
              value={draft.standardServingQty}
              onChange={(event) => set({ standardServingQty: event.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              select
              fullWidth
              label="Unit"
              value={draft.servingUnit}
              onChange={(event) => set({ servingUnit: event.target.value })}
            >
              {SERVING_UNITS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value.toLowerCase()}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Selling price"
              value={draft.sellingPrice}
              onChange={(event) => set({ sellingPrice: event.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="Cost estimate"
              value={draft.costEstimate}
              onChange={(event) => set({ costEstimate: event.target.value })}
              helperText="Drives the margin shown on quotations"
            />
          </Grid>

          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label="GST %"
              value={draft.taxRatePercent}
              onChange={(event) => set({ taxRatePercent: event.target.value })}
            />
          </Grid>
          {/* Whether the price above already has that GST in it. Nearly every
              product follows the business; a counter's shelf price does not. */}
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Price is"
              value={draft.pricing}
              onChange={(event) => set({ pricing: event.target.value })}
            >
              <MenuItem value="EXCLUSIVE">Excluding GST</MenuItem>
              <MenuItem value="INCLUSIVE">Including GST</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              fullWidth
              label="HSN code"
              placeholder="2106"
              value={draft.hsnCode}
              onChange={(event) => set({ hsnCode: event.target.value.replace(/[^0-9]/g, '') })}
              helperText="Printed against this product where the service has no SAC"
              inputProps={{ maxLength: 8 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={draft.liveCounter}
                  onChange={(event) => set({ liveCounter: event.target.checked })}
                />
              }
              label="Live counter — needs a chef and equipment at the venue"
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={sideOptions}
              loading={findingSides}
              // The server has already matched the term against code and name;
              // matching again here would narrow what it deliberately returned.
              filterOptions={(all) => all}
              onInputChange={(_event, next, reason) => {
                if (reason !== 'reset') setSideTerm(next)
              }}
              isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
              // The dishes already chosen, held as one array rather than
              // rebuilt each render. A multiple Autocomplete clears what is
              // being typed whenever its value changes, and a value that is a
              // fresh array every time changes on every render - so each
              // keystroke wiped the box it had just been typed into.
              // The catalogue is larger than any list of it, so an empty answer
              // means "not found", not "there are none".
              noOptionsText={
                sideTerm.trim() ? `Nothing matches "${sideTerm.trim()}"` : 'Type a name or a code'
              }
              value={chosenSides}
              getOptionLabel={(option) => `${option.itemCode} — ${option.name}`}
              onChange={(_event, value) => {
                // Held so a dish stays listed once the search moves off it.
                setChosenSides(value)
                set({
                  // A dish just added starts at the default weight; one removed
                  // takes its weight with it.
                  sideDishes: value.map((option) => ({
                    sideItemId: option.id,
                    grams:
                      draft.sideDishes.find((side) => side.sideItemId === option.id)?.grams ??
                      String(DEFAULT_SIDE_GRAMS),
                  })),
                })
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Side dishes"
                  placeholder="Search by name or code"
                  helperText="Dishes that go out with this one. The kitchen produces them alongside it."
                />
              )}
            />
          </Grid>

          {draft.sideDishes.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Grams of each side per serving of this dish
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {draft.sideDishes.map((side) => {
                    const dish = sideOptions.find((option) => option.id === side.sideItemId)
                    return (
                      <Stack
                        key={side.sideItemId}
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2" sx={{ minWidth: 0, flexGrow: 1 }} noWrap>
                          {dish ? `${dish.itemCode} — ${dish.name}` : side.sideItemId}
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={side.grams}
                          onChange={(event) =>
                            set({
                              sideDishes: draft.sideDishes.map((current) =>
                                current.sideItemId === side.sideItemId
                                  ? { ...current, grams: event.target.value }
                                  : current,
                              ),
                            })
                          }
                          sx={{ width: 120, flexShrink: 0 }}
                          InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                          inputProps={{ 'aria-label': `Grams of ${dish?.name ?? 'side dish'}` }}
                        />
                      </Stack>
                    )
                  })}
                </Stack>
              </Box>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Description"
              value={draft.description}
              onChange={(event) => set({ description: event.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!valid || save.isPending}>
          {item ? 'Save changes' : 'Create item'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

/** Categories are the buckets package selection rules are written against. */
/**
 * A menu category, new or being corrected.
 *
 * <p>The code is fixed once the category exists: every dish filed under it
 * refers to this row, so renaming what it is called is one thing and changing
 * which thing it is quite another.</p>
 */
export function MenuCategoryDialog({
  open,
  category,
  onClose,
}: {
  open: boolean
  category?: MenuCategoryRow | null
  onClose: () => void
}) {
  const save = useSaveMenuCategory()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [courseType, setCourseType] = useState('')
  const [sortOrder, setSortOrder] = useState('')
  const [offeredInPackages, setOffered] = useState(true)
  const [offeredAsSideDish, setSideDish] = useState(true)

  useEffect(() => {
    if (!open) return
    setCode(category?.code ?? '')
    setName(category?.name ?? '')
    setCourseType(category?.courseType ?? '')
    setSortOrder(category ? String(category.sortOrder ?? '') : '')
    setOffered(category ? category.offeredInPackages : true)
    setSideDish(category ? category.offeredAsSideDish : true)
  }, [open, category])

  const submit = () =>
    save.mutate(
      {
        id: category?.id,
        body: {
          code: code.trim().toUpperCase(),
          name: name.trim(),
          courseType: courseType || undefined,
          offeredInPackages,
          offeredAsSideDish,
          sortOrder: sortOrder ? Number(sortOrder) : undefined,
        },
      },
      { onSuccess: onClose },
    )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{category ? category.name : 'New menu category'}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={save.error} />
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              required
              disabled={Boolean(category)}
              label="Code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="STARTER"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              required
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Starters"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              fullWidth
              label="Course"
              value={courseType}
              onChange={(event) => setCourseType(event.target.value)}
            >
              <MenuItem value="">Not set</MenuItem>
              {['WELCOME', 'STARTER', 'MAIN', 'BREAD', 'RICE', 'DESSERT', 'BEVERAGE', 'SIDE'].map(
                (value) => (
                  <MenuItem key={value} value={value}>
                    {value.toLowerCase()}
                  </MenuItem>
                ),
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="number"
              label="Sort order"
              value={sortOrder}
              onChange={(event) => setSortOrder(event.target.value)}
              helperText="Order it appears on the menu card"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={offeredInPackages}
                  onChange={(event) => setOffered(event.target.checked)}
                />
              }
              label="Packages may offer these dishes"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Turn off for what nobody serves — a semi-finished good is a step in
              production, not a choice on a menu card.
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={offeredAsSideDish}
                  onChange={(event) => setSideDish(event.target.checked)}
                />
              }
              label="A dish's sides may come from here"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Turn off for what is never served beside something else. A side is
              usually a chutney, a gravy or an accompaniment, and the picker is a
              long scroll when every category is on offer.
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!code.trim() || !name.trim() || save.isPending}
        >
          {category ? 'Save' : 'Create category'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
